import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Pencil, Store, Info } from "lucide-react";
import SubmissionForm from "@/components/forms/SubmissionForm";
import RequestEditLinkCard from "@/components/forms/RequestEditLinkCard";
import ListingSearchSelect from "@/components/forms/ListingSearchSelect";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LangContext";

const HEADING = {
  nl: "Laat je bedrijf zien op Lemmer Leeft",
  de: "Zeigen Sie Ihr Unternehmen auf Lemmer Leeft",
  en: "Showcase your business on Lemmer Leeft",
};
const INTRO = {
  nl: "Sta jij met je bedrijf op Lemmer Leeft? Als lid van BIZ Vereniging Lemmer krijg je een gratis vermelding in de gids. Ook ondernemers van buiten de zone kunnen als donateur meedoen. Vul hieronder je gegevens in, dan zetten wij je bedrijf mooi in de schijnwerpers.",
  de: "Möchten Sie mit Ihrem Unternehmen auf Lemmer Leeft erscheinen? Als Mitglied von BIZ Vereniging Lemmer erhalten Sie einen kostenlosen Eintrag im Verzeichnis. Auch Unternehmer außerhalb der Zone können als Förderer mitmachen. Füllen Sie unten Ihre Daten aus, wir rücken Ihr Unternehmen ins Rampenlicht.",
  en: "Want your business featured on Lemmer Leeft? As a member of BIZ Vereniging Lemmer you get a free listing in the directory. Businesses outside the zone can also participate as a donor. Fill in your details below and we'll put your business in the spotlight.",
};
const SEO = {
  nl: { title: "Voor ondernemers — Lemmer Leeft", description: "Ben je ondernemer in Lemmer? Meld je bedrijf aan of vul je profiel aan op Lemmer Leeft, hét bezoekersportaal van Lemmer." },
  de: { title: "Für Unternehmer — Lemmer Leeft", description: "Sind Sie Unternehmer in Lemmer? Melden Sie Ihr Unternehmen an oder ergänzen Sie Ihr Profil auf Lemmer Leeft." },
  en: { title: "For businesses — Lemmer Leeft", description: "Business owner in Lemmer? Register or complete your profile on Lemmer Leeft, the visitor portal of Lemmer." },
};

export const VoorOndernemersPage = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [tab, setTab] = useState("nieuw");
  const [selectedListing, setSelectedListing] = useState(null);
  const seo = SEO[lang] || SEO.nl;

  const TABS = [
    { key: "nieuw", label: t("form.nieuw_aanmelden"), icon: Sparkles, tagline: t("form.ik_sta_er_niet_op") },
    { key: "aanvulling", label: t("form.profiel_aanvullen"), icon: Pencil, tagline: t("form.ik_sta_er_al_op") },
  ];

  return (
    <div data-testid="voor-ondernemers-page" className="bg-white">
      <Seo title={seo.title} description={seo.description} lang={lang} />
      {/* Header */}
      <section className="relative ll-section-soft py-14 sm:py-20 overflow-hidden">
        {/* subtiele golf-decoratie rechts */}
        <svg
          className="absolute -right-10 -bottom-10 opacity-30 pointer-events-none hidden sm:block"
          width="380"
          height="220"
          viewBox="0 0 380 220"
          aria-hidden="true"
        >
          <path
            d="M0,140 C80,180 160,100 240,130 C320,160 360,170 380,120 L380,220 L0,220 Z"
            fill="#6BC0E8"
          />
          <path
            d="M0,170 C80,210 160,140 240,165 C320,190 360,200 380,160 L380,220 L0,220 Z"
            fill="#2E9CD6"
            opacity="0.55"
          />
        </svg>

        <div className="ll-container max-w-4xl relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F3C300] text-[#173A5E] font-[Poppins] font-semibold text-xs uppercase tracking-wider mb-5">
            <Store className="w-3.5 h-3.5" />
            {t("nav.voor_ondernemers")}
          </div>
          <h1
            data-testid="ondernemers-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5"
          >
            {HEADING[lang] || HEADING.nl}
          </h1>
          <div
            className="flex items-center gap-3 mb-5"
            aria-hidden="true"
          >
            <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
            <svg
              width="60"
              height="10"
              viewBox="0 0 60 10"
              className="text-[#6BC0E8]"
            >
              <path
                d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-2xl">
            {INTRO[lang] || INTRO.nl}
          </p>
        </div>
      </section>

      {/* Tabs + formulier */}
      <section className="py-10 sm:py-14">
        <div className="ll-container max-w-4xl">
          {/* Tabs als kaarten */}
          <div
            data-testid="tab-cards"
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-10"
          >
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`tab-${t.key}`}
                  onClick={() => {
                    setTab(t.key);
                    if (t.key === "nieuw") setSelectedListing(null);
                  }}
                  className={`text-left rounded-2xl p-5 border transition-all ${
                    active
                      ? "border-[#2E9CD6] bg-white shadow-lg ring-2 ring-[#6BC0E8]/40"
                      : "border-[#E4EEF5] bg-white hover:border-[#6BC0E8] hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        active
                          ? "bg-[#2E9CD6] text-white"
                          : "bg-[#F4F8FB] text-[#2E9CD6]"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-1">
                        {t.tagline}
                      </p>
                      <h3 className="font-[Poppins] font-bold text-lg text-[#173A5E]">
                        {t.label}
                      </h3>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Formulier */}
          <div className="bg-white rounded-2xl border border-[#E4EEF5] shadow-sm p-6 sm:p-8">
            {tab === "nieuw" ? (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                    {t("form.nieuw_aanmelden")}
                  </h2>
                  <p className="text-[#3E5871] font-[Inter]">
                    {t("form.verplicht_hint")}
                  </p>
                </div>
                <SubmissionForm mode="nieuw" />
              </>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                    {t("form.profiel_aanvullen")}
                  </h2>
                  <p className="text-[#3E5871] font-[Inter]">
                    {t("form.aanvullen_intro")}
                  </p>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-2">
                    {t("form.zoek_je_bedrijf")}{" "}
                    <span className="text-[#CE1B2A]">*</span>
                  </label>
                  <ListingSearchSelect
                    selected={selectedListing}
                    onSelect={setSelectedListing}
                  />
                </div>

                {selectedListing ? (
                  <RequestEditLinkCard listing={selectedListing} key={selectedListing.slug} />
                ) : (
                  <div className="bg-[#F4F8FB] border border-dashed border-[#6BC0E8] rounded-xl p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-white mx-auto flex items-center justify-center mb-3 shadow-sm">
                      <Info className="w-5 h-5 text-[#2E9CD6]" />
                    </div>
                    <p className="text-[#3E5871] font-[Inter] max-w-md mx-auto">
                      {t("form.kies_eerst_bedrijf")}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VoorOndernemersPage;
